import React, { useEffect, useState, useRef } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import type { TourStep } from '@/hooks/useOnboarding'

interface OnboardingTourProps {
  currentStep: TourStep
  stepIndex: number
  totalSteps: number
  onNext: () => void
  onPrevious: () => void
  onSkip: () => void
}

export function OnboardingTour({
  currentStep,
  stepIndex,
  totalSteps,
  onNext,
  onPrevious,
  onSkip
}: OnboardingTourProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updatePosition = () => {
      const isMobile = window.innerWidth < 768

      if (currentStep.placement === 'center') {
        // Center tooltip on screen
        setPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2
        })
        setSpotlightRect(null)
        return
      }

      const targetElement = document.querySelector(currentStep.target) as HTMLElement
      
      if (!targetElement) {
        console.warn(`[ONBOARDING] Target element not found: ${currentStep.target}`)
        // Fallback to center
        setPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2
        })
        setSpotlightRect(null)
        return
      }

      const rect = targetElement.getBoundingClientRect()
      setSpotlightRect(rect)

      // On mobile, always center the tooltip and just highlight the element
      if (isMobile) {
        setPosition({
          top: window.innerHeight / 2,
          left: window.innerWidth / 2
        })
        // Scroll element into view
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        return
      }

      // Desktop positioning with smart placement to avoid covering target
      const tooltipWidth = tooltipRef.current?.offsetWidth || 320
      const tooltipHeight = tooltipRef.current?.offsetHeight || 200
      const padding = 20
      const viewportPadding = 20

      let top = 0
      let left = 0
      let adjustedPlacement = currentStep.placement

      // Calculate initial position based on placement
      switch (currentStep.placement) {
        case 'top':
          top = rect.top - tooltipHeight - padding
          left = rect.left + rect.width / 2
          break
        case 'bottom':
          top = rect.bottom + padding
          left = rect.left + rect.width / 2
          break
        case 'left':
          top = rect.top + rect.height / 2
          left = rect.left - tooltipWidth - padding
          break
        case 'right':
          top = rect.top + rect.height / 2
          left = rect.right + padding
          break
        default:
          top = rect.bottom + padding
          left = rect.left + rect.width / 2
      }

      // Smart repositioning to avoid covering target element
      const tooltipRect = {
        top,
        left,
        right: left + tooltipWidth,
        bottom: top + tooltipHeight
      }

      // Check if tooltip would cover the target element
      const coversTarget = (
        tooltipRect.left < rect.right &&
        tooltipRect.right > rect.left &&
        tooltipRect.top < rect.bottom &&
        tooltipRect.bottom > rect.top
      )

      if (coversTarget) {
        // Try alternative placements
        if (currentStep.placement === 'top' && rect.bottom + tooltipHeight + padding < window.innerHeight - viewportPadding) {
          // Move to bottom if there's space
          top = rect.bottom + padding
          adjustedPlacement = 'bottom'
        } else if (currentStep.placement === 'bottom' && rect.top - tooltipHeight - padding > viewportPadding) {
          // Move to top if there's space
          top = rect.top - tooltipHeight - padding
          adjustedPlacement = 'top'
        } else if (currentStep.placement === 'left' && rect.right + tooltipWidth + padding < window.innerWidth - viewportPadding) {
          // Move to right if there's space
          left = rect.right + padding
          top = rect.top + rect.height / 2
          adjustedPlacement = 'right'
        } else if (currentStep.placement === 'right' && rect.left - tooltipWidth - padding > viewportPadding) {
          // Move to left if there's space
          left = rect.left - tooltipWidth - padding
          top = rect.top + rect.height / 2
          adjustedPlacement = 'left'
        } else {
          // Fallback: position tooltip to the side with more space
          const spaceLeft = rect.left
          const spaceRight = window.innerWidth - rect.right
          
          if (spaceLeft > spaceRight) {
            // Position to the left
            left = Math.max(viewportPadding, rect.left - tooltipWidth - padding)
            top = rect.top + rect.height / 2
            adjustedPlacement = 'left'
          } else {
            // Position to the right
            left = Math.min(window.innerWidth - tooltipWidth - viewportPadding, rect.right + padding)
            top = rect.top + rect.height / 2
            adjustedPlacement = 'right'
          }
        }
      }

      // Keep tooltip within viewport bounds
      const maxTop = window.innerHeight - tooltipHeight - viewportPadding
      const maxLeft = window.innerWidth - tooltipWidth - viewportPadding
      
      top = Math.max(viewportPadding, Math.min(top, maxTop))
      left = Math.max(viewportPadding, Math.min(left, maxLeft))

      setPosition({ top, left })

      // Scroll element into view if needed
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    // Small delay to ensure DOM is ready
    const timer = setTimeout(updatePosition, 100)
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [currentStep])

  const isFirstStep = stepIndex === 0
  const isLastStep = stepIndex === totalSteps - 1

  return (
    <>
      {/* Backdrop with spotlight effect */}
      <div className="fixed inset-0 z-[9998] pointer-events-none animate-fade-in">
        <svg className="w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {spotlightRect && (
                <rect
                  x={spotlightRect.left - 8}
                  y={spotlightRect.top - 8}
                  width={spotlightRect.width + 16}
                  height={spotlightRect.height + 16}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.75)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      </div>

      {/* Spotlight ring animation */}
      {spotlightRect && (
        <div
          className="fixed z-[9999] pointer-events-none animate-pulse"
          style={{
            top: spotlightRect.top - 8,
            left: spotlightRect.left - 8,
            width: spotlightRect.width + 16,
            height: spotlightRect.height + 16
          }}
        >
          <div className="w-full h-full rounded-xl border-4 border-blue-500 shadow-[0_0_30px_rgba(59,130,246,0.6)]" />
        </div>
      )}

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={`fixed z-[10000] animate-scale-in transform -translate-x-1/2 ${
          currentStep.placement === 'center' || typeof window !== 'undefined' && window.innerWidth < 768
            ? '-translate-y-1/2' 
            : currentStep.placement === 'left' || currentStep.placement === 'right'
              ? '-translate-y-1/2'
              : ''
        }`}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
          maxWidth: '90vw',
          width: typeof window !== 'undefined' && window.innerWidth < 768 ? '85vw' : currentStep.placement === 'center' ? '400px' : '320px'
        }}
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 text-white">
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-bold pr-2">{currentStep.title}</h3>
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSkip(); }}
                className="text-white/80 hover:text-white transition-colors flex-shrink-0"
                aria-label="Close tour"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {currentStep.description}
            </p>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mt-6 mb-4">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div
                  key={i}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === stepIndex
                      ? 'w-8 bg-blue-600'
                      : i < stepIndex
                        ? 'w-2 bg-blue-400'
                        : 'w-2 bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPrevious(); }}
                disabled={isFirstStep}
                variant="outline"
                size="sm"
                className="flex-1"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              
              <Button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onSkip(); }}
                variant="ghost"
                size="sm"
                className="flex-1 text-gray-600 dark:text-gray-400"
              >
                Skip Tour
              </Button>

              <Button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext(); }}
                size="sm"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isLastStep ? 'Finish' : 'Next'}
                {!isLastStep && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>

            {/* Step counter */}
            <div className="text-center mt-3 text-xs text-gray-500 dark:text-gray-400">
              Step {stepIndex + 1} of {totalSteps}
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes fade-in {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes scale-in {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }

          .animate-fade-in {
            animation: fade-in 0.3s ease-out;
          }

          .animate-scale-in {
            animation: scale-in 0.3s ease-out;
          }
        `
      }} />
    </>
  )
}

